import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import HostingNav from '../components/layout/HostingNav';
import PageHeader from '../components/PageHeader';
import { FilterChip } from '../components/contentQueue/QueueFilters';
import { Badge, Button, Card, Checkbox, Icon, Input, SearchableSelect, useToast } from '../components/ui';
import type { BadgeTone } from '../components/ui';
import { useUser } from '../context/UserContext';
import { timeAgo } from '../lib/comms';
import { supabase } from '../lib/supabase';
import { fetchHostingAccounts, fetchMonitoredSites, saveHostingAccount, type HostedSite, type HostingAccount } from '../lib/hosting';
import {
  IGNORE_REASONS,
  assignInventorySite,
  fetchInventory,
  setInventoryDecision,
  undoAssign,
  type AssignUndo,
  type InventoryRow,
} from '../lib/hostingInventory';
import { bareHost, classify, rootDomain, suggestAccounts, type InventoryState, type InventoryWarning } from '../lib/siteInventoryRules.mjs';

type Filter = 'decide' | 'monitored' | 'unmonitored' | 'attached' | 'ignored' | 'warnings' | 'all';

const FILTERS: Array<{ id: Filter; label: string; title: string }> = [
  { id: 'decide', label: 'Needs a decision', title: 'Not on a hosting account and not ignored' },
  { id: 'monitored', label: 'Monitored, no account', title: 'In the uptime monitor, on no hosting account' },
  { id: 'unmonitored', label: 'Not monitored', title: 'Nothing watches it' },
  { id: 'attached', label: 'Attached', title: 'On a hosting account' },
  { id: 'ignored', label: 'Ignored', title: 'Marked as not ours to host' },
  { id: 'warnings', label: 'Warnings', title: 'Linked to a provider that no longer serves the domain, a leftover app, or duplicate monitor rows' },
  { id: 'all', label: 'All', title: 'Everything the providers list' },
];

const STATE: Record<InventoryState, { label: string; tone: BadgeTone }> = {
  attached: { label: 'Attached', tone: 'up' },
  monitored: { label: 'Monitored, no account', tone: 'info' },
  unmonitored: { label: 'Not monitored', tone: 'idle' },
  ignored: { label: 'Ignored', tone: 'neutral' },
};

const PROVIDER_LABEL = { netlify: 'Netlify', cloudways: 'Cloudways' } as const;
const NEW_ACCOUNT = '__new__';

const inFilter = (f: Filter, state: InventoryState, warnings: InventoryWarning[]) =>
  f === 'all' ||
  (f === 'decide' && (state === 'monitored' || state === 'unmonitored')) ||
  (f === 'warnings' && warnings.length > 0) ||
  f === state;

const shortDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null);

/** "outlawindustries.co" → "Outlaw Industries"-ish names are a guess; the domain's first label, capitalised, is honest. */
const nameFrom = (row: InventoryRow) => {
  const base = row.primary_domain ? rootDomain(row.primary_domain).split('.')[0] : row.name;
  return base.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/** What a person did to a row on this visit, so it stays in place with an Undo. */
type Recent = { kind: 'assigned'; accountId: string; undo: AssignUndo } | { kind: 'ignored'; reason: string };

/**
 * Site inventory: every Netlify site and Cloudways app, against the uptime monitor and the
 * hosting accounts, so each unassigned one gets a decision in a click or two: assign it to an
 * account (the likely one is offered first) or ignore it with a reason. The hosting runner syncs
 * the list (scripts/hosting-runner/lib/inventory.mjs); nothing here can reach the providers.
 */
export default function HostingInventory() {
  const [inventory, setInventory] = useState<InventoryRow[] | null>([]);
  const [sites, setSites] = useState<HostedSite[]>([]);
  const [accounts, setAccounts] = useState<HostingAccount[]>([]);
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [filter, setFilter] = useState<Filter>('decide');
  const [search, setSearch] = useState('');
  const [showNoDomain, setShowNoDomain] = useState(false);
  const [showGone, setShowGone] = useState(false);

  // One row open at a time: its account picker, or its ignore reasons.
  const [open, setOpen] = useState<{ id: string; mode: 'assign' | 'new' | 'ignore' } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [recent, setRecent] = useState<Record<string, Recent>>({});
  // Rows acted on stay in the list (with Undo) until the filter or search changes.
  const [sticky, setSticky] = useState<Set<string>>(new Set());
  const [focusId, setFocusId] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAccount, setBulkAccount] = useState<string | null>(null);

  const { user } = useUser();
  const toast = useToast();
  const by = user?.id ? { id: user.id, name: user.name || user.email || 'Someone' } : null;

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [inv, s, a, c] = await Promise.all([
        fetchInventory(),
        fetchMonitoredSites(),
        fetchHostingAccounts(),
        supabase.from('clients').select('id, name').order('name'),
      ]);
      setInventory(inv);
      setSites(s);
      setAccounts(a);
      setClients(c.data || []);
    } catch (err) {
      console.error('Error loading the site inventory:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to load the site inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSticky(new Set());
  }, [filter, search]);

  const accountName = useCallback((id: string | null) => accounts.find((a) => a.id === id)?.name ?? 'an account', [accounts]);

  // Every row with how it stands; gone rows only when asked for.
  const rows = useMemo(() => {
    const ctx = { accounts, sites, clients, inventory: inventory || [] };
    return (inventory || []).map((row) => {
      const c = classify(row, sites);
      return { row, ...c, suggestions: c.state === 'attached' || c.state === 'ignored' ? [] : suggestAccounts(row, ctx) };
    });
  }, [inventory, sites, accounts, clients]);

  const live = rows.filter((r) => !r.row.gone_at);
  const goneCount = rows.length - live.length;

  const counts = useMemo(() => {
    const out = {} as Record<Filter, number>;
    for (const f of FILTERS) out[f.id] = live.filter((r) => inFilter(f.id, r.state, r.warnings)).length;
    return out;
  }, [live]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (showGone ? rows : live)
      .filter((r) => sticky.has(r.row.id) || inFilter(filter, r.state, r.warnings))
      .filter(
        (r) =>
          !term ||
          [r.row.name, r.row.primary_domain, r.row.repo, r.row.server_label, ...(r.row.aliases || []), r.attachedTo ? accountName(r.attachedTo) : null]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(term)),
      );
  }, [rows, live, showGone, sticky, filter, search, accountName]);

  // By registrable domain, apex first, so admin.x.com sits under x.com and focus moves on to its sibling.
  const withDomain = visible
    .filter((r) => r.row.primary_domain)
    .sort((x, y) => {
      const hx = bareHost(x.row.primary_domain);
      const hy = bareHost(y.row.primary_domain);
      return rootDomain(hx).localeCompare(rootDomain(hy)) || hx.length - hy.length || hx.localeCompare(hy) || x.row.provider.localeCompare(y.row.provider);
    });
  const noDomain = visible.filter((r) => !r.row.primary_domain).sort((x, y) => x.row.name.localeCompare(y.row.name));
  // The order focus moves through: the no-domain group only counts when it is open.
  const order = [...withDomain, ...(showNoDomain ? noDomain : [])].map((r) => r.row.id);
  const lastSync = (inventory || []).reduce<string | null>((max, r) => (!max || r.seen_at > max ? r.seen_at : max), null);

  useEffect(() => {
    if (!focusId) return;
    const el = rowRefs.current[focusId];
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusId]);

  /** Focus the row after this one, as it was ordered before the action. */
  const advance = (id: string, before: string[]) => {
    const i = before.indexOf(id);
    setFocusId(before.slice(i + 1).find((x) => x !== id) ?? null);
  };

  const touch = (id: string, r: Recent | null) => {
    setRecent((m) => {
      const next = { ...m };
      if (r) next[id] = r;
      else delete next[id];
      return next;
    });
    setSticky((s) => new Set(s).add(id));
  };

  const reloadSites = async () => setSites(await fetchMonitoredSites());

  const assign = async (row: InventoryRow, accountId: string) => {
    const before = order;
    setBusy(row.id);
    try {
      const undo = await assignInventorySite(row, accountId, sites);
      touch(row.id, { kind: 'assigned', accountId, undo });
      setOpen(null);
      await reloadSites();
      advance(row.id, before);
    } catch (err) {
      console.error('Error assigning an inventory site:', err);
      toast.error(err instanceof Error ? err.message : 'Could not assign the site', bareHost(row.primary_domain) || row.name);
    } finally {
      setBusy(null);
    }
  };

  const createAndAssign = async (row: InventoryRow, name: string, clientId: string | null) => {
    setBusy(row.id);
    try {
      const account = await saveHostingAccount(null, {
        name: name.trim(),
        status: 'active',
        client_id: clientId,
        contact_name: null,
        contact_email: null,
        provider: PROVIDER_LABEL[row.provider],
        plan: null,
        monthly_price: null,
        started_on: null,
        notes: null,
      });
      setAccounts(await fetchHostingAccounts());
      toast.success('Hosting account created', account.name);
      setBusy(null);
      await assign(row, account.id);
    } catch (err) {
      console.error('Error creating a hosting account:', err);
      toast.error(err instanceof Error ? err.message : 'Could not create the account');
      setBusy(null);
    }
  };

  const ignore = async (row: InventoryRow, reason: string) => {
    const before = order;
    setBusy(row.id);
    try {
      await setInventoryDecision(row.id, 'ignored', reason, by);
      setInventory((inv) => inv?.map((r) => (r.id === row.id ? { ...r, decision: 'ignored', decision_note: reason, decided_by_name: by?.name ?? null, decided_at: new Date().toISOString() } : r)) ?? inv);
      touch(row.id, { kind: 'ignored', reason });
      setOpen(null);
      advance(row.id, before);
    } catch (err) {
      console.error('Error ignoring an inventory site:', err);
      toast.error(err instanceof Error ? err.message : 'Could not ignore the site');
    } finally {
      setBusy(null);
    }
  };

  const undo = async (row: InventoryRow, r: Recent | undefined, decision: InventoryRow['decision']) => {
    setBusy(row.id);
    try {
      if (r?.kind === 'assigned') {
        await undoAssign(r.undo);
        await reloadSites();
      } else if (decision === 'ignored') {
        await setInventoryDecision(row.id, null, null, null);
        setInventory((inv) => inv?.map((x) => (x.id === row.id ? { ...x, decision: null, decision_note: null, decided_by_name: null, decided_at: null } : x)) ?? inv);
      }
      touch(row.id, null);
      setFocusId(row.id);
    } catch (err) {
      console.error('Error undoing an inventory decision:', err);
      toast.error(err instanceof Error ? err.message : 'Could not undo');
    } finally {
      setBusy(null);
    }
  };

  const assignSelected = async () => {
    if (!bulkAccount) return;
    const targets = rows.filter((r) => selected.has(r.row.id) && r.state !== 'attached');
    setBusy('bulk');
    let done = 0;
    try {
      for (const t of targets) {
        // Re-read sites between rows: two rows can share a host.
        const fresh = await fetchMonitoredSites();
        const u = await assignInventorySite(t.row, bulkAccount, fresh);
        touch(t.row.id, { kind: 'assigned', accountId: bulkAccount, undo: u });
        done += 1;
      }
      toast.success(`${done} site${done === 1 ? '' : 's'} assigned`, accountName(bulkAccount));
      setSelected(new Set());
      setBulkAccount(null);
    } catch (err) {
      console.error('Error assigning sites:', err);
      toast.error(err instanceof Error ? err.message : 'Could not assign every site', `${done} of ${targets.length} assigned`);
    } finally {
      await reloadSites();
      setBusy(null);
    }
  };

  const toggleSelected = (id: string, on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const accountOptions = (suggestions: Array<{ accountId: string; reason: string }>) => {
    // The reason is on the one-click button's tooltip; here it would crowd out the account name.
    const first = suggestions.map((s) => ({ value: s.accountId, label: accountName(s.accountId), hint: 'Suggested' }));
    const rest = accounts
      .filter((a) => !suggestions.some((s) => s.accountId === a.id))
      .map((a) => ({ value: a.id, label: a.name, hint: clients.find((c) => c.id === a.client_id)?.name ?? a.provider ?? undefined }));
    return [...first, { value: NEW_ACCOUNT, label: 'New account…', hint: 'Create one for this site' }, ...rest];
  };

  const renderRow = (r: (typeof rows)[number], first: boolean) => (
    <InventoryRowView
      key={r.row.id}
      rowRef={(el) => {
        rowRefs.current[r.row.id] = el;
      }}
      item={r}
      first={first}
      focused={focusId === r.row.id}
      selected={selected.has(r.row.id)}
      onSelect={(on) => toggleSelected(r.row.id, on)}
      recent={recent[r.row.id]}
      accountName={accountName}
      busy={busy === r.row.id || busy === 'bulk'}
      open={open?.id === r.row.id ? open.mode : null}
      onOpen={(mode) => setOpen(mode ? { id: r.row.id, mode } : null)}
      options={accountOptions(r.suggestions)}
      clients={clients}
      onAssign={(accountId) => assign(r.row, accountId)}
      onCreate={(name, clientId) => createAndAssign(r.row, name, clientId)}
      onIgnore={(reason) => ignore(r.row, reason)}
      onUndo={() => undo(r.row, recent[r.row.id], r.row.decision)}
    />
  );

  const selectedCount = [...selected].filter((id) => rows.some((r) => r.row.id === id && r.state !== 'attached')).length;

  return (
    <div className="min-h-screen">
      <Sidebar />
      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader title="Site inventory" count={counts.decide} searchPlaceholder="Search domains, repos, accounts…" searchValue={search} onSearchChange={setSearch} />
        <HostingNav />

        {loadError ? (
          <Card>
            <div style={{ color: 'var(--status-down)', fontSize: 'var(--text-sm)' }}>{loadError}</div>
          </Card>
        ) : loading ? null : inventory === null ? (
          <Card>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--leading-normal)' }}>
              The site inventory isn’t set up yet. Once migration <code>20261103190000_hosting_inventory</code> is applied, the hosting runner fills it within the hour
              (or right away with <code>runner.mjs inventory --write</code>).
            </div>
          </Card>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px 16px', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {FILTERS.map((f) =>
                  f.id === 'warnings' && !counts.warnings ? null : (
                    <FilterChip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)} title={f.title}>
                      {f.label} <span className="t-num">{counts[f.id]}</span>
                    </FilterChip>
                  ),
                )}
              </div>
              <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', display: 'inline-flex', gap: '10px', alignItems: 'center' }}>
                {live.length} sites · synced {lastSync ? timeAgo(lastSync) : 'never'}
                {goneCount ? (
                  <button type="button" className="hover-underline" onClick={() => setShowGone((v) => !v)} style={linkButton}>
                    {showGone ? 'Hide' : 'Show'} {goneCount} gone
                  </button>
                ) : null}
              </span>
            </div>

            {selectedCount ? (
              <div
                className="flex flex-col sm:flex-row sm:items-center"
                style={{
                  position: 'sticky',
                  top: '8px',
                  zIndex: 5,
                  gap: '8px 12px',
                  marginBottom: 'var(--space-3)',
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--accent)',
                  background: 'var(--surface-raised)',
                  boxShadow: 'var(--shadow-pop)',
                }}
              >
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', whiteSpace: 'nowrap' }}>
                  {selectedCount} selected
                </span>
                <div style={{ flex: 1, minWidth: 0, maxWidth: '360px' }}>
                  <SearchableSelect
                    size="sm"
                    placeholder="Assign to account…"
                    searchPlaceholder="Search accounts…"
                    options={accounts.map((a) => ({ value: a.id, label: a.name, hint: clients.find((c) => c.id === a.client_id)?.name }))}
                    value={bulkAccount}
                    onChange={setBulkAccount}
                    menuMinWidth={300}
                  />
                </div>
                <span style={{ display: 'inline-flex', gap: '6px' }}>
                  <Button size="sm" disabled={!bulkAccount || busy !== null} onClick={assignSelected}>
                    {busy === 'bulk' ? 'Assigning…' : `Assign ${selectedCount}`}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} disabled={busy === 'bulk'}>
                    Clear
                  </Button>
                </span>
              </div>
            ) : null}

            {visible.length === 0 ? (
              <Card>
                <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                  {filter === 'decide' && !search ? 'Every site has a decision.' : 'Nothing matches.'}
                </div>
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {withDomain.length ? <div style={listBox}>{withDomain.map((r, i) => renderRow(r, i === 0))}</div> : null}
                {noDomain.length ? (
                  <div style={listBox}>
                    <button
                      type="button"
                      className="hover-wash"
                      aria-expanded={showNoDomain}
                      onClick={() => setShowNoDomain((v) => !v)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        width: '100%',
                        padding: '12px 14px',
                        border: 0,
                        background: 'transparent',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        textAlign: 'left',
                        color: 'var(--text-muted)',
                        fontSize: 'var(--text-sm)',
                      }}
                    >
                      <Icon name={showNoDomain ? 'chevron-down' : 'chevron-right'} size={14} />
                      No domain (dev/preview)
                      <span className="t-num" style={{ color: 'var(--text-faint)' }}>
                        {noDomain.length}
                      </span>
                      <span className="hidden md:inline" style={{ marginLeft: 'auto', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>Netlify sites with no custom domain, Cloudways apps with only their cloudwaysapps.com host</span>
                    </button>
                    {showNoDomain ? <div style={{ borderTop: '1px solid var(--border-hairline)' }}>{noDomain.map((r, i) => renderRow(r, i === 0))}</div> : null}
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const listBox: React.CSSProperties = {
  background: 'var(--surface-card)',
  border: '1px solid var(--border-hairline)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-rest)',
  overflow: 'hidden',
};

const linkButton: React.CSSProperties = {
  background: 'none',
  border: 0,
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 'var(--text-xs)',
  color: 'var(--accent-text)',
};

type RowItem = {
  row: InventoryRow;
  state: InventoryState;
  attachedTo: string | null;
  warnings: InventoryWarning[];
  suggestions: Array<{ accountId: string; reason: string }>;
};

type RowProps = {
  item: RowItem;
  first: boolean;
  focused: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
  recent: Recent | undefined;
  accountName: (id: string | null) => string;
  busy: boolean;
  open: 'assign' | 'new' | 'ignore' | null;
  onOpen: (mode: 'assign' | 'new' | 'ignore' | null) => void;
  options: Array<{ value: string; label: string; hint?: string }>;
  clients: Array<{ id: string; name: string }>;
  onAssign: (accountId: string) => void;
  onCreate: (name: string, clientId: string | null) => void;
  onIgnore: (reason: string) => void;
  onUndo: () => void;
  rowRef: (el: HTMLDivElement | null) => void;
};

/** One Netlify site or Cloudways app: what it is, how it stands, and its one-click actions. */
function InventoryRowView({ item, first, focused, selected, onSelect, recent, accountName, busy, open, onOpen, options, clients, onAssign, onCreate, onIgnore, onUndo, rowRef }: RowProps) {
  const { row, state, attachedTo, warnings, suggestions } = item;
  const top = suggestions[0];
  const decidable = state === 'monitored' || state === 'unmonitored';
  const leftOver = warnings.some((w) => w.kind === 'moved');
  const host = row.primary_domain ? bareHost(row.primary_domain) : null;
  const published = shortDate(row.last_published_at);
  const detail = [
    host ? row.name : null,
    row.provider === 'netlify' ? row.repo || 'no repo' : row.server_label,
    published ? `${row.provider === 'netlify' ? 'published' : 'created'} ${published}` : null,
  ].filter(Boolean);

  return (
    <div
      ref={rowRef}
      tabIndex={-1}
      style={{
        borderTop: first ? 'none' : '1px solid var(--border-hairline)',
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        outline: 'none',
        background: focused ? 'var(--surface-active)' : recent ? 'color-mix(in srgb, var(--surface-active) 50%, transparent)' : undefined,
        boxShadow: focused ? 'inset 3px 0 0 var(--accent)' : undefined,
        opacity: row.gone_at ? 0.6 : 1,
        transition: 'background var(--dur-base) var(--ease-out)',
      }}
    >
      <div className="flex flex-col md:flex-row md:items-center" style={{ gap: '8px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0, flex: 1 }}>
          <span style={{ paddingTop: '2px', visibility: decidable && !recent ? 'visible' : 'hidden' }}>
            <Checkbox checked={selected} onChange={onSelect} />
          </span>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minWidth: 0, flexWrap: 'wrap' }}>
              <Badge tone="neutral">{PROVIDER_LABEL[row.provider]}</Badge>
              {host ? (
                <a
                  href={`https://${host}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-underline"
                  style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--text-body)', overflowWrap: 'anywhere' }}
                >
                  {host}
                </a>
              ) : (
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--text-body)', overflowWrap: 'anywhere' }}>{row.name}</span>
              )}
              {row.aliases?.length ? (
                <span title={row.aliases.join('\n')} style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                  +{row.aliases.length} alias{row.aliases.length === 1 ? '' : 'es'}
                </span>
              ) : null}
            </span>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflowWrap: 'anywhere' }}>{detail.join(' · ')}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '2px' }}>
              {state === 'attached' && attachedTo ? (
                <Link to={`/hosting/${attachedTo}`} style={{ display: 'inline-flex', textDecoration: 'none' }} title="Open the hosting account">
                  <Badge tone="up" dot>
                    {accountName(attachedTo)}
                  </Badge>
                </Link>
              ) : (
                <Badge tone={STATE[state].tone} dot>
                  {state === 'ignored' && row.decision_note ? `Ignored · ${row.decision_note}` : STATE[state].label}
                </Badge>
              )}
              {warnings.map((w) => (
                <Badge key={w.kind} tone="warn" title={w.text}>
                  {w.kind === 'duplicate' ? 'Duplicate monitor rows' : w.kind === 'moved' ? 'Left over' : w.kind === 'linked_elsewhere' ? 'Linked elsewhere' : 'Not serving'}
                </Badge>
              ))}
              {row.serves_here === false && !warnings.length ? (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>Domain doesn’t answer from {PROVIDER_LABEL[row.provider]}</span>
              ) : null}
              {row.gone_at ? <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>Gone from {PROVIDER_LABEL[row.provider]} {timeAgo(row.gone_at)}</span> : null}
            </span>
            {warnings.length ? <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-warn)' }}>{warnings.map((w) => w.text).join(' · ')}</span> : null}
          </div>
        </div>

        <div className="md:justify-end" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', flexShrink: 0, paddingLeft: '30px' }}>
          {recent ? (
            <>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <Icon name="check" size={13} style={{ color: 'var(--status-up)' }} />
                {recent.kind === 'assigned' ? `Assigned to ${accountName(recent.accountId)}` : `Ignored · ${recent.reason}`}
              </span>
              <Button size="sm" variant="ghost" icon="rotate-ccw" disabled={busy} onClick={onUndo}>
                Undo
              </Button>
            </>
          ) : state === 'ignored' ? (
            <>
              {row.decided_by_name ? <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>by {row.decided_by_name}</span> : null}
              <Button size="sm" variant="ghost" icon="rotate-ccw" disabled={busy} onClick={onUndo}>
                Undo
              </Button>
            </>
          ) : state === 'attached' ? null : (
            <>
              {leftOver ? (
                // An old app still listing a domain that moved: retiring it is the likely call, not assigning it.
                <Button size="sm" icon="x" disabled={busy} onClick={() => onIgnore('Retired')}>
                  Ignore · Retired
                </Button>
              ) : top ? (
                <span title={`Suggested: ${top.reason}`} style={{ display: 'inline-flex', minWidth: 0 }}>
                  <Button size="sm" icon="arrow-right" disabled={busy} onClick={() => onAssign(top.accountId)}>
                    <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{accountName(top.accountId)}</span>
                  </Button>
                </span>
              ) : null}
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => onOpen(open === 'assign' ? null : 'assign')}>
                {top && !leftOver ? 'Other…' : 'Assign…'}
              </Button>
              {leftOver ? null : (
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => onOpen(open === 'ignore' ? null : 'ignore')}>
                  Ignore
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {open === 'assign' ? (
        <div style={{ paddingLeft: '30px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px', maxWidth: '420px' }}>
            <SearchableSelect
              size="sm"
              defaultOpen
              placeholder="Pick a hosting account…"
              searchPlaceholder="Search accounts, clients…"
              options={options}
              value={null}
              menuMinWidth={340}
              onChange={(v) => {
                if (v === NEW_ACCOUNT) onOpen('new');
                else if (v) onAssign(v);
              }}
            />
          </div>
          <Button size="sm" variant="ghost" onClick={() => onOpen(null)}>
            Cancel
          </Button>
        </div>
      ) : null}
      {open === 'new' ? <NewAccountForm row={row} suggestedClient={suggestedClient(row, clients)} clients={clients} busy={busy} onCreate={onCreate} onCancel={() => onOpen(null)} /> : null}
      {open === 'ignore' ? (
        <div style={{ paddingLeft: '30px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginRight: '4px' }}>Why?</span>
          {IGNORE_REASONS.map((reason) => (
            <FilterChip key={reason} active={false} onClick={() => !busy && onIgnore(reason)}>
              {reason}
            </FilterChip>
          ))}
          <Button size="sm" variant="ghost" onClick={() => onOpen(null)}>
            Cancel
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** The client whose name is the domain's first label (petespanels.com → Pete's Panels), to prefill a new account. */
function suggestedClient(row: InventoryRow, clients: Array<{ id: string; name: string }>) {
  if (!row.primary_domain) return null;
  const label = rootDomain(row.primary_domain).split('.')[0].replace(/[^a-z0-9]/gi, '').toLowerCase();
  return clients.find((c) => c.name.replace(/[^a-z0-9]/gi, '').toLowerCase() === label) ?? null;
}

/** "New account…": name and client prefilled, so creating one is a single click unless they're wrong. */
function NewAccountForm({
  row,
  suggestedClient: client,
  clients,
  busy,
  onCreate,
  onCancel,
}: {
  row: InventoryRow;
  suggestedClient: { id: string; name: string } | null;
  clients: Array<{ id: string; name: string }>;
  busy: boolean;
  onCreate: (name: string, clientId: string | null) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(client?.name ?? nameFrom(row));
  const [clientId, setClientId] = useState<string | null>(client?.id ?? null);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto]" style={{ paddingLeft: '30px', gap: '8px', alignItems: 'flex-end', maxWidth: '760px' }}>
      <Input label="New account" fieldSize="sm" value={name} onChange={(e) => setName(e.target.value)} />
      <SearchableSelect
        label="Client"
        size="sm"
        placeholder="No client"
        searchPlaceholder="Search clients…"
        clearLabel="No client"
        allowClear
        options={clients.map((c) => ({ value: c.id, label: c.name }))}
        value={clientId}
        onChange={setClientId}
      />
      <span style={{ display: 'inline-flex', gap: '6px' }}>
        <Button size="sm" icon="plus" disabled={!name.trim() || busy} onClick={() => onCreate(name, clientId)}>
          Create &amp; assign
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </span>
    </div>
  );
}
